import _ from "lodash";
import WebStoragePolyfill from "./webstorage-polyfill";
import Analytics from "../analytics";
import actions from "../actions/product";

export default [
  '$rootScope', '$timeout', '$q',
  'CartAPIService', 'CampaignService', 'ErrorHandler',
  class CartService {
    constructor($rootScope, $timeout, $q, cartAPIService, campaignService, ErrorHandler, webstorage = new WebStoragePolyfill(), analytics = new Analytics()) {
      this._$rootScope = $rootScope;
      this._$timeout = $timeout;
      this._$q = $q;
      this._cartAPIService = cartAPIService;
      this._campaignService = campaignService;
      this._errorHandler = ErrorHandler;
      this._webstorage = webstorage;
      this.analytics = analytics;

      this._readyDeferred = this._$q.defer();
      this.ready = this._readyDeferred.promise;

      this._initDefaults();
    }

    _initDefaults() {
      this.cart = {};
      this.coupon = {};
      this.totals = {};

      this.country = 'US';
      this.processing = false;
      this.getTotalPromiseComplete = true;

      this.previousCouponCode = null;
    }

    _localstoreCart() {
      if (this.cart.slug === null) return false;
      let cartObj = { coupon: this.coupon, contents: this.cart.contents };
      this._webstorage.setLocalItem(`${this.cart.slug}`, JSON.stringify(cartObj));
    }

    _getCartTotalsHelper(cartFunc) {
      let promise = cartFunc();

      this.processing = true;
      promise.then((res) => {
          this._setTotals(res);
          this.getTotalPromiseComplete = true;
        })
        .catch(err => {
          // -1 status means we cancelled it manually so we dont want to reset totals at this time.
          if (err.status && err.status !== -1) {
            this._errorHandler.throwError('Totals Error', _.get(err, 'data.message') ? err.data.message : 'Error updating cart totals.', { rawError: err });

            // if there was no discount amount before this error, reset the coupon object.
            if (!this.totals.discount) {
              this.coupon = {};
            } else if (this.totals.couponCode) {
              this.coupon.code = this.totals.couponCode;
            }

            if(_.get(err, 'data.message').match(/Coupon \[.*\] cannot be used on this order\./)) {
              this._localstoreCart();
              this._getCartTotals();
            }
          }
        })
        .finally(() => this.processing = false);

      return promise;
    }

    _getCartTotals() {
      let deferred = this._$q.defer(),
        promise;

      if (this.size() > 0) {
        let contents = _.map(this.cart.contents, (item) => {
          // Strip out data the API doesn't need.
          item = _.clone(item);
          delete item.price;
          delete item.thumbnail;
          return item;
        });
        promise = this._getCartTotalsHelper(() => this._cartAPIService.getTotal(this.campaign.id, this.coupon.code, this.state, this.country, contents));
      } else {
        promise = deferred.promise;
        deferred.resolve();
      }

      return promise;
    }

    _getAmazonCartTotals(amazonReferenceId, orderReferenceID) {
      return this._getCartTotalsHelper(() => this._cartAPIService.getAmazonTotal(amazonReferenceId, orderReferenceID, this.coupon.code));
    }

    _setTotals(cartData) {
      this.totals.items = cartData.totals;
      this.totals.discount = cartData.discount_amount;
      this.totals.checkout = cartData.sub_total;
      this.totals.shipping = cartData.shipping_cost;
      this.totals.shippingDiscount = cartData.shipping_discount;
      this.totals.tax = cartData.sales_tax;
      this.totals.grand = cartData.grand_total;
      this.totals.coupon = cartData.coupon;
      this.totals.couponCode = cartData.coupon ? cartData.coupon.code : null;

      this.coupon = cartData.coupon || {};
    }

    _applyCouponHelper(totalsFunc) {
      let deferred = this._$q.defer(),
        promise = deferred.promise,
        rejectData = {
          data: {
            message: 'Please enter a coupon code.'
          }
        };

      if (this.coupon.code) {
        this.processing = true;

        this._campaignService.validateCoupon(this.username, this.campaignSlug, this.coupon.code)
          .then(res => {
            if (res.valid) {
              this.couponType = res.type;
              this.couponAmount = res.amount;

              if (this.size() > 0)
                totalsFunc();
            } else {
              this.coupon = {};
              this._localstoreCart();
              totalsFunc();
            }
            deferred.resolve(res);
          })
          .catch(() => {
            this.coupon = {};
            this._localstoreCart();
            totalsFunc();
            deferred.resolve();
          })
          .finally(() => this.processing = false);
      } else {
        if (this.totals.couponCode) rejectData.data.couponCode = this.totals.couponCode;
        this.processing = true;
        deferred.reject(rejectData);
      }

      return promise;
    }

    init(username, campaignSlug) {
      if (!this.cart.initialized) {
        this.username = username;
        this.campaignSlug = campaignSlug;
        this.cart.slug = `${username}/${campaignSlug}`;
        let cartObj = JSON.parse(this._webstorage.getLocalItem(`${this.cart.slug}`)) || {};

        if (Array.isArray(cartObj)) this.cart.contents = cartObj;
        else if (!Array.isArray(cartObj) && Array.isArray(cartObj.contents)) {
          this.coupon = (typeof cartObj.coupon == 'object') ? cartObj.coupon : {};
          this.cart.contents = cartObj.contents;
        } else this.cart.contents = [];

        this._campaignService.getBySlug(username, campaignSlug)
          .then((data) => {
            this.campaign = data.campaign;

            // remove any inactive products
            let products = actions.filterInactiveProducts(this.campaign.products);
            data.campaign.products = products;
            this.campaign.products = products;

            // remove any inactive products from exisiting cart and save the changes
            this.cart.contents = _.filter(this.cart.contents, item => _.find(this.campaign.products, product => product.id === item.cpid));
            this._localstoreCart();

            if (this.cart.contents.length > 0 && this.campaign.id !== this.cart.contents[0].cid) {
              this.cart.contents = [];
            }

            if (this.size() > 0) {
              if (Object.keys(this.coupon).length > 0) {
                this._applyCouponHelper(() => this._getCartTotals()).then(() => this._readyDeferred.resolve(data));
              } else {
                this._getCartTotals().then(() => this._readyDeferred.resolve(data));
              }
            } else {
              this._readyDeferred.resolve(data);
            }

            this.cart.initialized = true;
          })
          .catch((err) => {
            this._errorHandler.throwError('Unable to Find Campaign', 'There was a problem loading this campaign.', {
              cart: cartObj,
              rawError: err
            });
            this._readyDeferred.reject();
          });
      } else {
        this._readyDeferred.resolve({
          campaign: this.campaign
        });
      }

      return this.ready;
    }

    contents() {
      return this.cart.contents;
    }

    contentsAsCampaignProducts() {
      let products = [];

      if (this.cart.contents) {
        for (var i = 0; i < this.cart.contents.length; i++) {
          let product = _.clone(_.find(this.campaign.products, { id: this.cart.contents[i].cpid }));
          product.qty = this.cart.contents[i].qty;
          product.size = this.cart.contents[i].size;
          if (product) products.push(product);
        }
      }

      return products;
    }

    add(product, size, qty) {
      if (this.cart.slug === null) return false;
      qty = parseInt(qty);
      let cartSearch = _.find(this.cart.contents, (item) => item.cpid === product.id && item.size === size);

      if (cartSearch !== undefined) {
        cartSearch.qty += qty;
      } else {
        this.cart.contents.push({
          cid: this.campaign.id,
          cpid: product.id,
          baseProduct: product.product.id,
          thumbnail: (product.default_side_to_display === 'BACK') ? product.assets.back_thumbnail : product.assets.front_thumbnail,
          size: size,
          qty: qty,
          price: (parseFloat(product.selling_price) + parseFloat(product.product.upcharges[size])).toFixed(2),
          name: `${product.color.name} ${product.product.name}`,
          group: product.product.group,
          specialPrinting: this.campaign.requires_special_printing,
        });
      }

      this._localstoreCart();

      if (this.getTotalPromiseComplete) {
        this.getTotalPromiseComplete = false;
        this._getCartTotals();
      } else {
        this._cartAPIService.cancelGetTotal();
        this._getCartTotals();
      }
    }

    remove(index) {
      if (this.cart.slug === null) return false;

      if (index === 'all') {
        this.cart.contents = [];
      } else {
        let product = _.find(this.campaign.products, {
          id: this.cart.contents[index].cpid
        });
        if (product) {
          this.analytics.removeFromCart(product, this.cart.contents[index].qty, this.cart.contents[index].size);
        }
        this.cart.contents.splice(index, 1);
      }

      this._localstoreCart();

      if (this.getTotalPromiseComplete) {
        this.getTotalPromiseComplete = false;
        this._getCartTotals();
      } else {
        this._cartAPIService.cancelGetTotal();
        this._getCartTotals();
      }
    }

    clearCart(cartSlug) {
      this._webstorage.removeLocalItem(cartSlug);
    }

    size() {
      if (this.cart.slug === null) return false;

      let size = 0;

      for (var i = 0; i < this.cart.contents.length; i++) {
        size += this.cart.contents[i].qty;
      }

      return size;
    }

    isProcessing() {
      return this.processing;
    }

    applyCoupon(couponCode, amazonReferenceId, orderReferenceID) {
      let promise

      if (!this.previousCouponCode) this.previousCouponCode = this.coupon.code;
      this.coupon.code = couponCode;

      if (amazonReferenceId) {
        promise = this._applyCouponHelper(() => this._getAmazonCartTotals(amazonReferenceId, orderReferenceID));
      } else {
        promise = this._applyCouponHelper(() => this._getCartTotals());
      }
      promise.then(res => {
        this._localstoreCart();

        if (res.valid && this.coupon.code !== this.previousCouponCode) {
          this.previousCouponCode = this.coupon.code;
          this.analytics.applyCoupon(this.coupon.code);
        }
      });

      return promise;
    }

    validateCouponBeforeCheckout() {
      let deferred = this._$q.defer(),
        promise = deferred.promise;

      if (Object.keys(this.coupon).length > 0) {
        this._applyCouponHelper(() => this._getCartTotals())
          .then(res => {
            if (res.valid) {
              deferred.resolve({ valid: true });
            } else {
              deferred.reject({ valid: false });
            }
          })
          .catch(err => {
            this._errorHandler.throwError('Coupon Error', 'An error occured while attempting to validate coupon. Please try again.', { rawError: err });
            deferred.reject();
          });
      } else {
        deferred.resolve({ valid: true });
      }

      return promise;
    }

    setCountry(country, state) {
      let promise,
        deferred = this._$q.defer(),
        prevCountry = this.country,
        prevState = this.state;

      this.country = country;
      this.state = state;

      if (this.cart.contents.length > 0 && (this.country !== prevCountry || (this.state !== prevState))) {
        promise = this._getCartTotals();
      } else {
        promise = deferred.promise;
        deferred.resolve({});
      }

      return promise;
    }

    setPixels(pixels) {
      this.pixels = pixels;
    }

    getTotals() {
      return this.totals;
    }

    getCouponInfo() {
      return {
        type: this.couponType,
        amount: this.couponAmount
      };
    }

    getAmazonTotals(amazonReferenceID, orderReferenceID) {
      this._getAmazonCartTotals(amazonReferenceID, orderReferenceID);
      return this.totals;
    }

    triggerInitiateCheckout() {
      this.analytics.initiateCheckout(this.campaign, this.size());
    }

    paypalCheckoutCart(callbackUrl, cancelUrl) {
      let purchases = [];
      this.processing = true;
      this.size();

      _.forEach(this.cart.contents, (item) => {
        purchases.push({
          cpid: item.cpid,
          qty: item.qty,
          size: item.size
        });
      });

      let payload = {
        shipping_country: this.country,
        coupon_code: this.coupon.code,
        purchases: purchases,
        success_callback_url: callbackUrl,
        cancel_callback_url: cancelUrl
      };

      let promise = this._cartAPIService.paypalCheckout(payload);

      promise.finally(() => this.processing = false);

      return promise;
    }

    paypalCheckoutUpsellCart(callbackUrl, cancelUrl, selectedProduct, upsellCampaignID, orderID) {
      let payload = {
        shipping_country: this.country,
        purchases: [selectedProduct],
        success_callback_url: callbackUrl,
        cancel_callback_url: cancelUrl,
        upsell_campaign_id: upsellCampaignID,
        original_order_id: orderID
      };

      let promise = this._cartAPIService.paypalUpsellCheckout(payload);

      return promise;
    }

    amazonCheckoutCart(amazonReferenceId, access_token) {
      this.size();

      let purchases = [];
      _.forEach(this.cart.contents, (item) => {
        purchases.push({
          product_id: item.cpid,
          quantity: item.qty,
          size: item.size,
          cid: this.campaign.id // Ugh.
        });
      });

      return this._cartAPIService.amazonCheckout({
        campaign_id: this.campaign.id,
        purchases: purchases,
        ref_id: amazonReferenceId,
        access_token: access_token
      });
    }

    amazonCheckoutUpsellCart(amazonReferenceId, access_token, selectedProduct, campaignID, upsellCampaignID, orderID) {
      return this._cartAPIService.amazonCheckout({
        campaign_id: campaignID,
        purchases: [selectedProduct],
        ref_id: amazonReferenceId,
        access_token: access_token,
        upsell_campaign_id: upsellCampaignID,
        order_type: 'UPSELL',
        order_id: orderID
      });
    }

    createCCTokenCart(card, customer) {
      this.processing = true;

      let promise = this._cartAPIService.createCCToken(card, customer);

      promise
        .then(res => this.analytics.placeOrder({ option: res.merchant, cart: this.contentsAsCampaignProducts() }))
        .catch(err => this._errorHandler.throwError('Payment Processing', err && err.message ? err.message : 'Error creating credit card token.', { rawError: err }));

      return promise;
    }

    _orderPayload(token, customer) {
      customer.name = `${customer.firstName} ${customer.lastName}`;

      return {
        cid: this.campaign.id,
        customer,
        card: {
          token
        },
        coupon_code: this.coupon.code,
        products: _.map(this.cart.contents, item => {
          return { product_id: item.cpid, quantity: item.qty, size: item.size };
        })
      }
    }

    processCCPayment(token, customer, card) {
      let payload = this._orderPayload(token, customer);
      payload.card.cc_bin = card.cc_num.replace(/\s/g, '').substr(0, 6);

      let promise = this._cartAPIService.placeOrder(payload);

      promise.catch((err) => this._errorHandler.throwError('Payment Processing', 'Error processing credit card payment.', {
          rawError: err
        }))
        .finally(() => this.processing = false);

      return promise;
    }

    processSavedPayment(token, customer) {
      let promise = this._cartAPIService.placeOrder(this._orderPayload(token, customer));

      promise.catch((err) => this._errorHandler.throwError('Payment Processing', 'Error processing saved credit card payment.'))
        .finally(() => this.processing = false);

      return promise;
    }

    processAmazonPaymentCart(id, orderReferenceID) {
      this.processing = true;

      let promise = this._cartAPIService.processAmazonPayment(id, orderReferenceID, this.coupon.code);

      promise.catch((err) => this._errorHandler.throwError('Amazon Payment Processing', 'Unable to process payment at this time.', {
          rawError: err
        }))
        .finally(() => this.processing = false);

      return promise;
    }

    slug() {
      return this.cart.slug;
    }
  }
];


// WEBPACK FOOTER //
// ./source/client/data-providers/cart-service.js